/**
 * Repository for Complex Process Definitions
 * 
 * Handles all data operations for complex processes
 */

import { ComplexProcessMutations } from '../mutations/ComplexProcessMutations';
import { ComplexProcessQueries } from '../queries/ComplexProcessQueries';
import { ComplexProcessTransformer } from '../transformers/ComplexProcessTransformer';
import { ComplexProcessDefinition, ProcessConnection, ProcessStage } from '../types/ComplexProcess';
import BaseRepository, { FilterParams, PaginationParams, SortParams } from './BaseRepository';

export class ComplexProcessRepository extends BaseRepository<ComplexProcessDefinition> {
    private transformer = new ComplexProcessTransformer();

    /**
     * Find all complex processes with optional filtering
     */
    async findAll(params?: {
        filter?: FilterParams;
        pagination?: PaginationParams;
        sort?: SortParams;
    }): Promise<ComplexProcessDefinition[]> {
        const response = await this.query<ComplexProcessDefinition[]>(
            ComplexProcessQueries.LIST_PROCESSES,
            {
                tenant: this.tenant,
                filter: params?.filter,
                pagination: params?.pagination || { offset: 0, limit: 100 },
                sort: params?.sort
            },
            {
                cache: true,
                cacheTime: 60000, // 1 minute
                transform: (data: any) => this.transformer.transformList(data.listComplexProcesses)
            }
        );

        return response;
    }

    /**
     * Find a single process by ID
     */
    async findById(id: string): Promise<ComplexProcessDefinition | null> {
        const response = await this.query<ComplexProcessDefinition | null>(
            ComplexProcessQueries.GET_PROCESS,
            { tenant: this.tenant, id },
            {
                cache: true,
                cacheTime: 300000, // 5 minutes
                transform: (data: any) => this.transformer.transformSingle(data.getComplexProcess)
            }
        );

        return response;
    }

    /**
     * Find a single process by tenant and ID (FERP Repository Pattern Compliance)
     */
    async findByTenantAndId(tenantId: string, id: string): Promise<ComplexProcessDefinition | null> {
        // Log access for audit trail
        await this.auditTrail.logAccess('COMPLEX_PROCESS_ACCESS', {
            tenantId,
            id,
            operation: 'findByTenantAndId',
            timestamp: new Date().toISOString()
        });

        const response = await this.query<ComplexProcessDefinition | null>(
            ComplexProcessQueries.GET_PROCESS,
            { tenant: tenantId, id },
            {
                cache: true,
                cacheTime: 300000, // 5 minutes
                transform: (data: any) => this.transformer.transformSingle(data.getComplexProcess)
            }
        );

        return response;
    }

    /**
     * Find processes by domain
     */
    async findByDomain(domain: string): Promise<ComplexProcessDefinition[]> {
        return this.findAll({
            filter: { domain }
        });
    }

    /**
     * Find template processes
     */
    async findTemplates(): Promise<ComplexProcessDefinition[]> {
        return this.findAll({
            filter: { isTemplate: true }
        });
    }

    /**
     * Find processes by complexity
     */
    async findByComplexity(complexity: 'Low' | 'Medium' | 'High' | 'Very High'): Promise<ComplexProcessDefinition[]> {
        return this.findAll({
            filter: { complexity }
        });
    }

    /**
     * Create a new process
     */
    async create(data: Partial<ComplexProcessDefinition>): Promise<ComplexProcessDefinition> {
        const response = await this.mutation<{ createComplexProcess: any }>(
            ComplexProcessMutations.CREATE_PROCESS,
            {
                tenant: this.tenant,
                input: {
                    ...data,
                    createdBy: sessionStorage.getItem('user') || 'current-user',
                    metadata: {
                        ...data.metadata,
                        createdAt: new Date().toISOString()
                    }
                }
            },
            {
                onSuccess: () => {
                    // Invalidate list cache
                    this.cache.invalidate('LIST_PROCESSES');
                }
            }
        );

        return this.transformer.transformSingle((response as any).createComplexProcess);
    }

    /**
     * Update an existing process
     */
    async update(id: string, data: Partial<ComplexProcessDefinition>): Promise<ComplexProcessDefinition> {
        const response = await this.mutation<{ updateComplexProcess: any }>(
            ComplexProcessMutations.UPDATE_PROCESS,
            {
                tenant: this.tenant,
                id,
                input: {
                    ...data,
                    metadata: {
                        ...data.metadata,
                        updatedAt: new Date().toISOString()
                    }
                }
            },
            {
                onSuccess: () => {
                    // Invalidate specific and list cache
                    this.cache.invalidate(`GET_PROCESS:${id}`);
                    this.cache.invalidate('LIST_PROCESSES');
                }
            }
        );

        return this.transformer.transformSingle((response as any).updateComplexProcess);
    }

    /**
     * Delete a process
     */
    async delete(id: string): Promise<boolean> {
        const response = await this.mutation<{ deleteComplexProcess: boolean }>(
            ComplexProcessMutations.DELETE_PROCESS,
            {
                tenant: this.tenant,
                id
            },
            {
                onSuccess: () => {
                    // Invalidate caches
                    this.cache.invalidate(`GET_PROCESS:${id}`);
                    this.cache.invalidate('LIST_PROCESSES');
                }
            }
        );

        return (response as any).deleteComplexProcess;
    }

    /**
     * Clone a template process
     */
    async cloneTemplate(templateId: string, newName: string): Promise<ComplexProcessDefinition> {
        const response = await this.mutation<{ cloneProcessTemplate: any }>(
            ComplexProcessMutations.CLONE_TEMPLATE,
            {
                templateId,
                targetTenant: this.tenant,
                newName
            },
            {
                onSuccess: () => {
                    // Invalidate list cache
                    this.cache.invalidate('LIST_PROCESSES');
                }
            }
        );

        return this.transformer.transformSingle((response as any).cloneProcessTemplate);
    }

    /**
     * Add a stage to a process
     */
    async addStage(processId: string, stage: ProcessStage): Promise<ComplexProcessDefinition> {
        const process = await this.findById(processId);
        if (!process) {
            throw new Error('Process not found');
        }

        const updatedStages = [...process.stages, stage];
        return this.update(processId, { stages: updatedStages });
    }

    /**
     * Remove a stage from a process
     */
    async removeStage(processId: string, stageId: string): Promise<ComplexProcessDefinition> {
        const process = await this.findById(processId);
        if (!process) {
            throw new Error('Process not found');
        }

        const updatedStages = process.stages.filter(s => s.id !== stageId);
        const updatedConnections = process.connections.filter(
            c => c.from !== stageId && c.to !== stageId
        );

        return this.update(processId, {
            stages: updatedStages,
            connections: updatedConnections
        });
    }

    /**
     * Add a connection between stages
     */
    async addConnection(processId: string, connection: ProcessConnection): Promise<ComplexProcessDefinition> {
        const process = await this.findById(processId);
        if (!process) {
            throw new Error('Process not found');
        }

        const updatedConnections = [...process.connections, connection];
        return this.update(processId, { connections: updatedConnections });
    }

    /**
     * Calculate process metrics
     */
    async getMetrics(processId: string): Promise<any> {
        const process = await this.findById(processId);
        if (!process) {
            throw new Error('Process not found');
        }

        return {
            totalSteps: process.stages.reduce((sum, stage) => sum + stage.steps.length, 0),
            uniqueActors: [...new Set(process.stages.flatMap(s => s.actors))].length,
            loopingStages: process.stages.filter(s => s.loops).length,
            externalIntegrations: process.externalSystems.length,
            decisionPoints: process.stages.filter(s => s.stageType === 'Decision').length,
            avgComplexity: this.calculateComplexity(process)
        };
    }

    private calculateComplexity(process: ComplexProcessDefinition): number {
        // Simple complexity calculation
        const factors = {
            stages: process.stages.length * 0.3,
            connections: process.connections.length * 0.2,
            actors: process.actors.length * 0.2,
            loops: process.stages.filter(s => s.loops).length * 0.15,
            external: process.externalSystems.length * 0.15
        };

        return Object.values(factors).reduce((sum, val) => sum + val, 0);
    }

    /**
     * Search processes by text
     */
    async search(query: string): Promise<ComplexProcessDefinition[]> {
        // This would ideally be a specialized GraphQL query
        const all = await this.findAll();

        const lowerQuery = query.toLowerCase();
        return all.filter(process =>
            process.name.toLowerCase().includes(lowerQuery) ||
            process.description.toLowerCase().includes(lowerQuery) ||
            process.domain.toLowerCase().includes(lowerQuery)
        );
    }

    /**
     * Get process statistics
     */
    async getStatistics(): Promise<any> {
        const all = await this.findAll();

        return {
            total: all.length,
            byDomain: this.groupBy(all, 'domain'),
            byComplexity: this.groupBy(all, 'complexity'),
            templates: all.filter(p => p.isTemplate).length,
            custom: all.filter(p => !p.isTemplate).length,
            active: all.filter(p => p.active).length
        };
    }

    private groupBy(items: any[], key: string): Record<string, number> {
        return items.reduce((acc, item) => {
            const value = item[key];
            acc[value] = (acc[value] || 0) + 1;
            return acc;
        }, {});
    }
}

// Export singleton instance
export const complexProcessRepository = new ComplexProcessRepository();

export default ComplexProcessRepository;
